import React from "react";
import { useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import { HashLoader } from "react-spinners";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import OutlinedInput from "@mui/material/OutlinedInput";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import { styled } from "@mui/material/styles";

import { MainButton } from "../../components/MainButton";
import { COLORS } from "../../utils/colors";
import { updateUser } from "../../store/mainReducer";
import { getOrgByEmail } from "../../api/user";
import { ICONS } from "../../utils/icons";
import { createTicket } from "../../api/tickets";

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: 250,
      minWidth: 250,
      fontFamily: "AuthenticSans90",
    },
  },
};

const requiredFields1 = [
  {
    label: "Customer Email",
    name: "email",
    type: "text",
  },
  {
    label: "Price paid by customer",
    name: "price",
    type: "number",
  },
];

const Android12Switch = styled(Switch)(({ theme }) => ({
  padding: 8,
  "& .MuiSwitch-track": {
    borderRadius: 22 / 2,
    "&:before, &:after": {
      content: '""',
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      width: 16,
      height: 16,
    },
    "&:before": {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
        theme.palette.getContrastText(theme.palette.primary.main)
      )}" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/></svg>')`,
      left: 12,
    },
    "&:after": {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
        theme.palette.getContrastText(theme.palette.primary.main)
      )}" d="M19,13H5V11H19V13Z" /></svg>')`,
      right: 12,
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "none",
    width: 16,
    height: 16,
    margin: 2,
  },
}));

export const CreateTicketDialog = ({
  openPopup,
  setOpenPopup,
  apiKey,
  userData,
  handleSnackbar,
  fetchData,
  event,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const formik = useFormik({
    initialValues: {
      email: "",
      price: "",
      category: "",
      create_customer: true,
    },
    validationSchema: yup.object({
      category: yup.string("Select ticket category"),
      //   .required("Required"),
      email: yup.string().email("Enter customer email").required("Required"),
      price: yup.number("Enter price").required("Required"),
    }),
    onSubmit: (values) => {
      handleFormSubmit(values);
    },
  });

  const handleFormSubmit = async (values) => {
    try {
      console.log(values);
      setLoading(true);

      // /**
      //  * Send to backend
      //  */
      const ticketData = {
        customer_email: values.email,
        event_id: event._id,
        event_name: event.name,
        create_new_customer: values.create_customer,
        tickets: [
          {
            category_name: values.category,
            current_price: values.price,
            current_currency: selectedCategory.currency,
            type: "general_admission",
          },
        ],
      };
      const { data, error } = await createTicket({
        apiKey,
        data: ticketData,
      });

      if (data) {
        handleSnackbar("Ticket created!", "success");

        const response = await getOrgByEmail(userData?.email);

        if (response.status === 200) {
          dispatch(updateUser(response.data));
          setSuccess(true);

          await fetchData();
        }
      }

      if (error) {
        console.log(error);
        handleSnackbar(error.message, "error");
      }

      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpenPopup(false);
  };

  return (
    <Dialog open={openPopup} onClose={() => handleClose()}>
      <DialogContent sx={{ width: "400px" }}>
        <h2 style={{ marginBottom: "5px" }}>
          {ICONS.ticketBlack20} Create Ticket
        </h2>
        <p>Create a ticket on behalf of your customer</p>
        <div style={{ height: "20px" }} />
        <form>
          <div className="two-columns-fields-container">
            <div style={{ width: "-webkit-fill-available" }}>
              <div className="field-container">
                <p
                  id="subheader"
                  style={{
                    margin: "0px 0px 3px",
                    alignSelf: "center",
                  }}
                >
                  Ticket Category*
                </p>
                <Select
                  size="small"
                  name="category"
                  value={formik.values.category}
                  onChange={(e) => {
                    formik.handleChange(e);
                    setSelectedCategory(
                      event.ticket_categories.find(
                        (category) => category.name === e.target.value
                      )
                    );
                  }}
                  input={<OutlinedInput label="Name" />}
                  MenuProps={MenuProps}
                  style={{
                    maxWidth: "100px",
                    minWidth: "-webkit-fill-available",
                  }}
                  sx={{
                    "& label": {
                      paddingLeft: (theme) => theme.spacing(1),
                    },
                    "& fieldset": {
                      borderRadius: "6px",
                      border: `2px solid #f0f1f5`,
                      transition: ".2s ease-in-out all",
                    },
                    "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                      {
                        borderColor: "#f0f1f5",
                      },
                    "& .MuiOutlinedInput-input:hover": {
                      cursor: loading && "not-allowed",
                    },
                  }}
                >
                  {event.ticket_categories.map((category, index) => {
                    return (
                      <MenuItem
                        key={index}
                        value={category.name}
                        style={{ fontFamily: "AuthenticSans90" }}
                      >
                        {category.name}
                      </MenuItem>
                    );
                  })}
                </Select>
              </div>
              {selectedCategory && (
                <div
                  style={{
                    background: COLORS.evenLighterGrey,
                    padding: 20,
                    marginBottom: 10,
                    borderRadius: 6,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      gap: "5px",
                      justifyContent: "space-between",
                      marginBottom: "10px",
                      paddingBottom: "10px",
                      borderBottom: "1px solid #e0e0e0",
                    }}
                  >
                    <p id="subheader">Total supply</p>
                    <p id="subheaderBlack"> {selectedCategory?.supply.total}</p>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      gap: "5px",
                      justifyContent: "space-between",
                      marginBottom: "10px",
                      paddingBottom: "10px",
                      borderBottom: "1px solid #e0e0e0",
                    }}
                  >
                    <p id="subheader">Sold to date:</p>
                    <p id="subheaderBlack"> {selectedCategory?.supply.sold}</p>
                  </div>
                </div>
              )}
              {requiredFields1.map((field, index) => {
                return (
                  <div key={index} className="field-container">
                    <p
                      id="subheader"
                      style={{
                        margin: "0px 0px 3px",
                        alignSelf: "center",
                      }}
                    >
                      {field.label}*
                    </p>
                    <TextField
                      fullWidth
                      disabled={loading}
                      type={field.type === "text" ? "text" : "number"}
                      size="small"
                      name={field.name}
                      placeholder="not set"
                      value={formik.values[field.name]}
                      onChange={formik.handleChange}
                      error={
                        formik.touched[field.name] &&
                        Boolean(formik.errors[field.name])
                      }
                      helperText={
                        formik.touched[field.name] && formik.errors[field.name]
                      }
                      sx={{
                        "& label": {
                          paddingLeft: (theme) => theme.spacing(1),
                        },
                        "& fieldset": {
                          borderRadius: "6px",
                          border: `2px solid #f0f1f5`,
                          transition: ".2s ease-in-out all",
                        },
                        "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                          {
                            borderColor: "#f0f1f5",
                          },
                        "& .MuiOutlinedInput-input:hover": {
                          cursor: loading && "not-allowed",
                        },
                      }}
                    />
                  </div>
                );
              })}
              <div className="field-container">
                <p
                  id="subheader"
                  style={{
                    margin: "0px 0px 3px",
                    alignSelf: "center",
                  }}
                >
                  Automatically create customer account if it doesn't exist
                </p>
                <FormControlLabel
                  control={
                    <Android12Switch
                      name={"create_customer"}
                      checked={formik.values.create_customer}
                      onClick={formik.handleChange}
                    />
                  }
                  label="No/Yes"
                />
              </div>
            </div>
          </div>
          <DialogActions>
            <MainButton
              disabled={loading || success}
              color={COLORS.black}
              shadowColor={"transparent"}
              textColor={COLORS.white}
              textHoverColor={COLORS.lightGrey}
              hoverColor={COLORS.black}
              textAlignment={"center"}
              onClickAction={() => formik.handleSubmit()}
              width={"fit-content"}
            >
              {loading ? (
                <>
                  <HashLoader size={20} color={COLORS.grey} /> Create
                </>
              ) : success ? (
                "Success"
              ) : (
                "Create"
              )}
            </MainButton>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
};
